<template>
    <div class="flex items-center gap-2 select-none" :class="sent ? 'text-white' : 'text-gray-800'" style="min-width: 180px; max-width: 240px;">
        <!-- Play / Pause -->
        <button
            type="button"
            @click="togglePlay"
            class="shrink-0 flex items-center justify-center rounded-full focus:outline-none transition-colors"
            :class="sent ? 'bg-white text-blue-600' : 'text-white'"
            :style="playButtonStyle"
        >
            <svg v-if="!isPlaying" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 ml-0.5">
                <path d="M8 5v14l11-7z"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
                <path d="M6 5h4v14H6zM14 5h4v14h-4z"/>
            </svg>
        </button>

        <div class="flex flex-col flex-1 min-w-0 gap-1">
            <!-- Waveform -->
            <div
                ref="wave"
                class="flex items-center gap-[2px] h-6 cursor-pointer w-full"
                @click="seekFromEvent"
            >
                <span
                    v-for="(bar, i) in bars"
                    :key="i"
                    class="rounded-full w-[3px] transition-colors duration-75"
                    :style="barStyle(bar, i)"
                ></span>
            </div>
            <!-- Time -->
            <div class="text-[11px] leading-none tabular-nums" :class="sent ? 'text-blue-100' : 'text-gray-500'">
                {{ timeText }}
            </div>
        </div>

        <audio
            ref="audio"
            :src="src"
            preload="metadata"
            class="hidden"
            @loadedmetadata="onLoadedMetadata"
            @timeupdate="onTimeUpdate"
            @play="isPlaying = true"
            @pause="isPlaying = false"
            @ended="onEnded"
        ></audio>
    </div>
</template>

<script>
const BAR_COUNT = 40;

// One AudioContext shared across all voice messages for decoding peaks.
let sharedAudioContext = null;
function getAudioContext() {
    if (sharedAudioContext) return sharedAudioContext;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    sharedAudioContext = new Ctx();
    return sharedAudioContext;
}

export default {
    name: 'VoiceMessage',

    props: {
        src: { type: String, required: true },
        // duration in seconds, if known from the message payload
        duration: { type: [Number, String], default: 0 },
        // true when the message was sent by the current guest (blue bubble)
        sent: { type: Boolean, default: false },
        // accent color used for the active waveform on received messages
        color: { type: String, default: '#2563eb' }
    },

    data() {
        return {
            isPlaying: false,
            currentTime: 0,
            totalDuration: Number(this.duration) || 0,
            bars: this.fallbackBars(),
            playbackId: Math.random().toString(36).slice(2)
        };
    },

    computed: {
        progress() {
            if (!this.totalDuration) return 0;
            return Math.min(1, this.currentTime / this.totalDuration);
        },

        playButtonStyle() {
            const base = { width: '36px', height: '36px' };
            if (!this.sent) {
                base.backgroundColor = this.color;
            }
            return base;
        },

        timeText() {
            // While playing show elapsed, otherwise show total length.
            const seconds = this.isPlaying || this.currentTime > 0
                ? this.currentTime
                : this.totalDuration;
            return this.formatTime(seconds);
        }
    },

    mounted() {
        this.loadWaveform();
        window.addEventListener('voice-message-play', this.onOtherPlay);
    },

    beforeDestroy() {
        window.removeEventListener('voice-message-play', this.onOtherPlay);
        const audio = this.$refs.audio;
        if (audio && !audio.paused) {
            audio.pause();
        }
    },

    methods: {
        togglePlay() {
            const audio = this.$refs.audio;
            if (!audio) return;

            if (audio.paused) {
                window.dispatchEvent(new CustomEvent('voice-message-play', { detail: this.playbackId }));
                const playPromise = audio.play();
                if (playPromise && playPromise.catch) {
                    playPromise.catch(() => { this.isPlaying = false; });
                }
            } else {
                audio.pause();
            }
        },

        // Pause this player when another voice message starts.
        onOtherPlay(event) {
            if (event.detail === this.playbackId) return;
            const audio = this.$refs.audio;
            if (audio && !audio.paused) audio.pause();
        },

        onLoadedMetadata() {
            const audio = this.$refs.audio;
            if (audio && isFinite(audio.duration) && audio.duration > 0) {
                this.totalDuration = audio.duration;
            }
        },

        onTimeUpdate() {
            const audio = this.$refs.audio;
            if (audio) this.currentTime = audio.currentTime;
        },

        onEnded() {
            this.isPlaying = false;
            this.currentTime = 0;
            const audio = this.$refs.audio;
            if (audio) audio.currentTime = 0;
        },

        seekFromEvent(event) {
            const el = this.$refs.wave;
            const audio = this.$refs.audio;
            if (!el || !audio || !this.totalDuration) return;

            const rect = el.getBoundingClientRect();
            const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
            audio.currentTime = ratio * this.totalDuration;
            this.currentTime = audio.currentTime;
        },

        barStyle(value, index) {
            const filled = index / BAR_COUNT <= this.progress;
            let bg;
            if (this.sent) {
                bg = filled ? '#ffffff' : 'rgba(255,255,255,0.45)';
            } else {
                bg = filled ? this.color : 'rgba(0,0,0,0.18)';
            }
            // value is 0..1 amplitude -> height between 20% and 100%
            const height = Math.round((0.2 + value * 0.8) * 24);
            return {
                height: height + 'px',
                backgroundColor: bg
            };
        },

        formatTime(seconds) {
            const total = Math.max(0, Math.floor(Number(seconds) || 0));
            const m = Math.floor(total / 60);
            const s = total % 60;
            return `${m}:${s < 10 ? '0' : ''}${s}`;
        },

        // Deterministic, natural-looking waveform used before/instead of real decode.
        fallbackBars() {
            const seed = this.hashString(this.src || 'voice');
            const bars = [];
            for (let i = 0; i < BAR_COUNT; i++) {
                // pseudo-random but stable per message
                const n = Math.sin((i + 1) * 12.9898 + seed) * 43758.5453;
                const frac = n - Math.floor(n);
                bars.push(0.25 + frac * 0.75);
            }
            return bars;
        },

        hashString(str) {
            let h = 0;
            for (let i = 0; i < str.length; i++) {
                h = (h << 5) - h + str.charCodeAt(i);
                h |= 0;
            }
            return Math.abs(h % 1000) / 1000;
        },

        // Try to decode the real audio for accurate peaks; fall back silently.
        async loadWaveform() {
            const ctx = getAudioContext();
            if (!ctx || !this.src) return;

            try {
                const res = await fetch(this.src);
                if (!res.ok) return;
                const arrayBuffer = await res.arrayBuffer();
                const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

                if (!this.totalDuration && audioBuffer.duration) {
                    this.totalDuration = audioBuffer.duration;
                }

                this.bars = this.computePeaks(audioBuffer);
            } catch (e) {
                // CORS or decode failure -> keep deterministic fallback bars
            }
        },

        computePeaks(audioBuffer) {
            const channel = audioBuffer.getChannelData(0);
            const blockSize = Math.floor(channel.length / BAR_COUNT) || 1;
            const peaks = [];
            let max = 0;

            for (let i = 0; i < BAR_COUNT; i++) {
                const start = i * blockSize;
                let sum = 0;
                for (let j = 0; j < blockSize; j++) {
                    sum += Math.abs(channel[start + j] || 0);
                }
                const avg = sum / blockSize;
                peaks.push(avg);
                if (avg > max) max = avg;
            }

            // normalize 0..1
            return peaks.map(p => (max > 0 ? p / max : 0));
        }
    }
};
</script>
